import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthContext";
import { usePlanLimits } from "../../../hooks/usePlanLimits";
import { getUsageSummaryForMonth } from "@/shared/db";
import { getFeatureName, getFeatureNameEs } from "@/shared/core";
import type { FeatureType } from "@/shared/core";
import { Button, Card } from "@/shared/ui";
import { ROUTES } from "@/shared/config";
import { useNavigate } from "react-router-dom";
import {
  ChartBarIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  BriefcaseIcon,
  DocumentMagnifyingGlassIcon,
  SparklesIcon,
  CalendarDaysIcon,
  ArrowDownTrayIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const FEATURE_TYPES: FeatureType[] = [
  "document_generation",
  "consultation",
  "case",
  "document_review",
  "case_summary",
  "legal_ai_chat",
  "contract_generation",
];

const FEATURE_ICONS: Record<FeatureType, React.ElementType> = {
  document_generation: DocumentTextIcon,
  consultation: ChatBubbleLeftRightIcon,
  case: BriefcaseIcon,
  exercise: DocumentTextIcon,
  exam: DocumentTextIcon,
  simulation: DocumentTextIcon,
  document_review: DocumentMagnifyingGlassIcon,
  case_summary: BriefcaseIcon,
  legal_ai_chat: SparklesIcon,
  contract_generation: DocumentTextIcon,
};

export const BillingReportsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { limits, loading: limitsLoading } = usePlanLimits();

  const [activeTab, setActiveTab] = useState<"overview" | "details">(
    "overview",
  );
  const [periodFilter, setPeriodFilter] = useState<"1m" | "3m" | "6m">("6m");
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<Date>(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [usageSummary, setUsageSummary] = useState<Record<string, number>>({});
  const [trendData, setTrendData] = useState<
    Array<{
      month: string;
      monthKey: string;
      total: number;
      usage: Record<string, number>;
    }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [trendLoading, setTrendLoading] = useState(false);

  const hasBillingReports = limits?.has_billing_reports === true;
  const hasAdvancedAnalytics = limits?.has_advanced_analytics === true;

  useEffect(() => {
    if (user && hasBillingReports) {
      loadUsage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedMonth, hasBillingReports]);

  useEffect(() => {
    if (user && hasAdvancedAnalytics && periodFilter !== "1m") {
      loadTrend();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasAdvancedAnalytics, periodFilter, i18n.language]);

  const loadUsage = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const summary = await getUsageSummaryForMonth(user.id, selectedMonth);
      setUsageSummary(summary);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Error loading usage:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadTrend = async () => {
    if (!user || periodFilter === "1m") return;
    setTrendLoading(true);
    try {
      const now = new Date();
      const monthsCount = periodFilter === "3m" ? 3 : 6;
      const data: typeof trendData = [];

      for (let i = monthsCount - 1; i >= 0; i--) {
        const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const usage = await getUsageSummaryForMonth(user.id, month);
        const total = Object.values(usage).reduce((a, b) => a + b, 0);
        data.push({
          month: month.toLocaleDateString(i18n.language, {
            month: "short",
            year: "2-digit",
          }),
          monthKey: month.toISOString().slice(0, 7),
          total,
          usage,
        });
      }

      setTrendData(data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Error loading trend:", err);
    } finally {
      setTrendLoading(false);
    }
  };

  const getFeatureLabel = (ft: FeatureType) =>
    i18n.language === "es" ? getFeatureNameEs(ft) : getFeatureName(ft);

  const getMonths = () => {
    const months: Date[] = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      months.push(new Date(now.getFullYear(), now.getMonth() - i, 1));
    }
    return months;
  };

  const chartData = trendData.map((d) => ({
    name: d.month,
    total: d.total,
    ...Object.fromEntries(FEATURE_TYPES.map((ft) => [ft, d.usage[ft] || 0])),
  }));

  const hasChartData = chartData.some((d) => d.total > 0);

  const thisMonthTotal = Object.values(usageSummary).reduce((a, b) => a + b, 0);
  const topFeatureEntry = FEATURE_TYPES.reduce<[FeatureType, number] | null>(
    (best, ft) => {
      const count = usageSummary[ft] || 0;
      if (!best || count > best[1]) return [ft, count];
      return best;
    },
    null,
  );
  const topFeature = topFeatureEntry?.[0] ?? null;
  const topFeatureCount = topFeatureEntry?.[1] ?? 0;

  const exportUsageCSV = useCallback(() => {
    const rows: string[][] = [["month", "feature", "count"]];
    if (periodFilter === "1m") {
      for (const ft of FEATURE_TYPES) {
        const count = usageSummary[ft] ?? 0;
        rows.push([selectedMonth.toISOString().slice(0, 7), ft, String(count)]);
      }
    } else {
      for (const d of trendData) {
        for (const ft of FEATURE_TYPES) {
          const count = d.usage[ft] ?? 0;
          rows.push([d.monthKey, ft, String(count)]);
        }
      }
    }
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `usage-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [periodFilter, trendData, usageSummary, selectedMonth]);

  if (limitsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500"
          aria-label={t("common.loading")}
        />
      </div>
    );
  }

  if (!hasBillingReports) {
    return (
      <div className="mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard/lawyer")}
          className="mb-6 min-h-[44px] min-w-[44px] transition-colors duration-200 hover:bg-romance-50 focus-visible:ring-2 focus-visible:ring-tiber-500 focus-visible:ring-offset-2 rounded-lg cursor-pointer"
          aria-label={t("common.back")}
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2 shrink-0" />
          <span className="hidden sm:inline">{t("common.back")}</span>
        </Button>
        <Card className="border-2 border-dorado/30 bg-linear-to-br from-dorado/5 to-romance-50 overflow-hidden">
          <div className="p-6 sm:p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-dorado/10 mb-4">
              <ChartBarIcon className="w-8 h-8 text-dorado" aria-hidden />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-juridico mb-2">
              {t("analytics.upgradeRequired")}
            </h2>
            <p className="text-tiber-700 mb-6 max-w-md mx-auto">
              {t("analytics.upgradeDescription")}
            </p>
            <Button
              variant="primary"
              onClick={() => navigate(ROUTES.PRICING)}
              className="min-h-[44px] px-6 transition-all duration-200 hover:shadow-md focus-visible:ring-2 focus-visible:ring-tiber-500 focus-visible:ring-offset-2 cursor-pointer"
            >
              {t("analytics.viewPlans")}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto">
      <h1 className="text-2xl sm:text-3xl font-bold text-juridico flex items-center gap-3 mb-4 sm:mb-6">
        <span className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-tiber-500/10 shrink-0">
          <ChartBarIcon
            className="w-6 h-6 sm:w-7 sm:h-7 text-tiber-500"
            aria-hidden
          />
        </span>
        {t("analytics.title")}
      </h1>

      {/* Tabs */}
      <div
        className="flex gap-1 p-1 rounded-xl bg-tiber-100/60 border border-tiber-200/60 w-fit mb-6 sm:mb-8"
        role="tablist"
      >
        <button
          role="tab"
          aria-selected={activeTab === "overview"}
          aria-controls="analytics-overview-panel"
          id="tab-overview"
          onClick={() => setActiveTab("overview")}
          className={`min-h-[44px] px-4 sm:px-5 rounded-lg font-medium text-sm transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-tiber-500 focus-visible:ring-offset-2 cursor-pointer ${
            activeTab === "overview"
              ? "bg-white text-juridico shadow-sm border border-tiber-200"
              : "text-tiber-700 hover:text-juridico hover:bg-white/50"
          }`}
        >
          {t("analytics.tabs.overview")}
        </button>
        <button
          role="tab"
          aria-selected={activeTab === "details"}
          aria-controls="analytics-details-panel"
          id="tab-details"
          onClick={() => setActiveTab("details")}
          className={`min-h-[44px] px-4 sm:px-5 rounded-lg font-medium text-sm transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-tiber-500 focus-visible:ring-offset-2 cursor-pointer ${
            activeTab === "details"
              ? "bg-white text-juridico shadow-sm border border-tiber-200"
              : "text-tiber-700 hover:text-juridico hover:bg-white/50"
          }`}
        >
          {t("analytics.tabs.details")}
        </button>
      </div>

      {/* Overview tab */}
      <div
        id="analytics-overview-panel"
        role="tabpanel"
        aria-labelledby="tab-overview"
        hidden={activeTab !== "overview"}
        className={activeTab !== "overview" ? "sr-only" : ""}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
          {/* Hero card - This month */}
          <div className="relative overflow-hidden rounded-lg bg-linear-to-br from-juridico via-tiber-950 to-juridico p-6 sm:p-8 shadow-xl">
            <div
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                backgroundSize: "24px 24px",
              }}
            />
            <div className="relative z-10">
              <p className="text-white/60 text-sm font-medium uppercase tracking-wider">
                {t("analytics.thisMonth")}
              </p>
              <p className="text-4xl sm:text-5xl font-black text-white tabular-nums mt-1">
                {loading ? "—" : thisMonthTotal}
              </p>
              <p className="text-white/50 text-sm mt-0.5">
                {t("analytics.usage")}
              </p>
            </div>
          </div>

          {/* Top feature card */}
          <Card className="px-4 border-2 border-tiber-200 overflow-hidden flex flex-col justify-center">
            <p className="text-tiber-600 text-sm font-medium uppercase tracking-wider">
              {t("analytics.topFeature")}
            </p>
            <div className="flex items-center gap-3 mt-2">
              {topFeature ? (
                <>
                  {(() => {
                    const Icon = FEATURE_ICONS[topFeature];
                    return (
                      <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-tiber-500/10 shrink-0">
                        <Icon className="w-6 h-6 text-tiber-500" aria-hidden />
                      </span>
                    );
                  })()}
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-juridico tabular-nums">
                      {topFeatureCount}
                    </p>
                    <p className="text-tiber-700 font-medium">
                      {topFeature ? getFeatureLabel(topFeature) : "—"}
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-titan text-sm">{t("analytics.noData")}</p>
              )}
            </div>
          </Card>
        </div>

        {/* Usage trend: pills + export (Professional Pro only) */}
        {hasAdvancedAnalytics ? (
          <section className="mb-10" aria-labelledby="trend-heading">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h2
                id="trend-heading"
                className="text-lg font-semibold text-juridico"
              >
                {t("analytics.trend")}
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex gap-1 p-1 rounded-lg bg-tiber-100/60 border border-tiber-200/60">
                  {(["1m", "3m", "6m"] as const).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPeriodFilter(p)}
                      className={`min-h-[38px] px-3 rounded-md text-sm font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-tiber-500 focus-visible:ring-offset-2 cursor-pointer ${
                        periodFilter === p
                          ? "bg-tiber-500 text-white"
                          : "text-tiber-700 hover:bg-white/60"
                      }`}
                    >
                      {t(`analytics.period${p}`)}
                    </button>
                  ))}
                </div>
                <Button
                  variant="secondary"
                  onClick={exportUsageCSV}
                  className="min-h-[38px] gap-2 shrink-0"
                  aria-label={t("analytics.exportCSV")}
                >
                  <ArrowDownTrayIcon className="w-5 h-5" aria-hidden />
                  <span className="hidden sm:inline">
                    {t("analytics.exportCSV")}
                  </span>
                </Button>
              </div>
            </div>

            {periodFilter === "1m" ? (
              <p className="text-titan text-sm py-4">
                {t("analytics.selectPeriodForTrend")}
              </p>
            ) : trendLoading ? (
              <div className="flex items-center justify-center min-h-[320px] rounded-2xl bg-linear-to-br from-tiber-50 to-romance-50 border-2 border-tiber-200">
                <div
                  className="animate-spin rounded-full h-12 w-12 border-2 border-tiber-200 border-t-tiber-500"
                  aria-label={t("common.loading")}
                />
              </div>
            ) : hasChartData ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="relative overflow-hidden rounded-2xl bg-linear-to-br from-juridico via-tiber-950 to-juridico p-6 sm:p-8 shadow-xl"
              >
                <div
                  className="absolute inset-0 opacity-[0.03]"
                  style={{
                    backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                    backgroundSize: "24px 24px",
                  }}
                />
                <div className="relative z-10 h-[260px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={chartData}
                      margin={{ top: 16, right: 16, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="usageGradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#1cb0a9"
                            stopOpacity={0.5}
                          />
                          <stop
                            offset="50%"
                            stopColor="#148d8a"
                            stopOpacity={0.3}
                          />
                          <stop
                            offset="100%"
                            stopColor="#148d8a"
                            stopOpacity={0}
                          />
                        </linearGradient>
                        <linearGradient
                          id="usageStroke"
                          x1="0"
                          y1="0"
                          x2="1"
                          y2="0"
                        >
                          <stop offset="0%" stopColor="#1cb0a9" />
                          <stop offset="100%" stopColor="#35ccc3" />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="name"
                        stroke="rgba(255,255,255,0.5)"
                        style={{ fontSize: "11px", fontWeight: 500 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        stroke="rgba(255,255,255,0.5)"
                        style={{ fontSize: "11px", fontWeight: 500 }}
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                        tick={{ fill: "rgba(255,255,255,0.6)" }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#ffffff",
                          border: "2px solid var(--color-tiber-200)",
                          borderRadius: "12px",
                          boxShadow: "0 10px 40px -12px rgba(10,28,46,0.25)",
                          padding: "12px 16px",
                          color: "var(--color-juridico)",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                        labelStyle={{
                          color: "var(--color-juridico)",
                          fontWeight: 600,
                        }}
                        itemStyle={{
                          color: "var(--color-tiber-700)",
                        }}
                        formatter={(value: number | undefined) => [
                          value ?? 0,
                          t("analytics.totalUsage"),
                        ]}
                        cursor={{
                          stroke: "var(--color-tiber-300)",
                          strokeWidth: 1,
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="total"
                        stroke="url(#usageStroke)"
                        strokeWidth={2.5}
                        fill="url(#usageGradient)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div
                  className="absolute bottom-0 left-0 right-0 h-24 bg-linear-to-t from-tiber-500/10 to-transparent pointer-events-none"
                  aria-hidden
                />
                {lastUpdated && (
                  <p className="relative z-10 text-white/50 text-xs mt-4">
                    {t("analytics.lastUpdate")}:{" "}
                    {lastUpdated.toLocaleTimeString(i18n.language, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    ,{" "}
                    {lastUpdated.toLocaleDateString(i18n.language, {
                      day: "2-digit",
                      month: "2-digit",
                      year: "2-digit",
                    })}
                  </p>
                )}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-16 text-center rounded-2xl border-2 border-dashed border-tiber-200 bg-tiber-50/50"
              >
                <ChartBarIcon className="w-12 h-12 mx-auto text-titan mb-3" />
                <p className="text-tiber-700 font-medium">
                  {t("analytics.noTrendData")}
                </p>
                <p className="text-titan text-sm mt-1">
                  {t("analytics.noTrendHint")}
                </p>
              </motion.div>
            )}
          </section>
        ) : (
          <section className="mb-10" aria-labelledby="trend-locked-heading">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2
                id="trend-locked-heading"
                className="text-lg font-semibold text-juridico"
              >
                {t("analytics.trend")}
              </h2>
              <span className="inline-flex items-center gap-1 rounded-full bg-dorado/10 text-dorado-dark px-3 py-1 text-xs font-semibold">
                <LockClosedIcon className="w-3.5 h-3.5" />
                Professional Pro
              </span>
            </div>

            <div className="relative overflow-hidden rounded-2xl border-2 border-dorado/30 bg-linear-to-br from-juridico via-tiber-950 to-juridico">
              {/* Blurred preview content */}
              <div className="pointer-events-none select-none blur-[3px] opacity-70">
                <div className="p-6 sm:p-8">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <div className="flex gap-1 p-1 rounded-lg bg-tiber-100/60 border border-tiber-200/60">
                      {(["1m", "3m", "6m"] as const).map((p) => (
                        <span
                          key={p}
                          className="px-3 min-h-[38px] inline-flex items-center rounded-md text-sm font-medium bg-white/70 text-tiber-700"
                        >
                          {t(`analytics.period${p}`)}
                        </span>
                      ))}
                    </div>
                    <span className="min-h-[38px] px-3 inline-flex items-center gap-2 rounded-md text-sm font-medium bg-white/70 text-tiber-700">
                      <ArrowDownTrayIcon className="w-4 h-4" />
                      {t("analytics.exportCSV")}
                    </span>
                  </div>
                  <div className="h-[260px] w-full rounded-xl bg-white/10" />
                </div>
              </div>

              {/* Upgrade overlay */}
              <div className="absolute inset-0 flex items-center justify-center p-5 sm:p-8">
                <Card className="w-full max-w-md border-2 border-dorado/40 bg-white/95 backdrop-blur-sm shadow-xl">
                  <div className="p-5 text-center">
                    <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-dorado/10 mb-3">
                      <SparklesIcon className="w-6 h-6 text-dorado-dark" />
                    </div>
                    <h3 className="text-lg font-bold text-juridico mb-1">
                      {t("analytics.upgradeRequired")}
                    </h3>
                    <p className="text-sm text-tiber-700 mb-4">
                      {t("analytics.upgradeDescription")}
                    </p>
                    <Button
                      variant="primary"
                      onClick={() => navigate(ROUTES.PRICING)}
                      className="w-full sm:w-auto"
                    >
                      {t("analytics.viewPlans")}
                    </Button>
                  </div>
                </Card>
              </div>
            </div>
          </section>
        )}
      </div>

      {/* Details tab */}
      <div
        id="analytics-details-panel"
        role="tabpanel"
        aria-labelledby="tab-details"
        hidden={activeTab !== "details"}
        className={activeTab !== "details" ? "sr-only" : ""}
      >
        <section className="mb-10" aria-labelledby="details-heading">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h2
              id="details-heading"
              className="text-lg font-semibold text-juridico"
            >
              {t("analytics.usageByMonth")}
            </h2>
            <div className="flex items-center gap-2">
              <CalendarDaysIcon
                className="w-5 h-5 text-tiber-600 shrink-0"
                aria-hidden
              />
              <label htmlFor="month-select" className="sr-only">
                {t("analytics.selectMonth")}
              </label>
              <select
                id="month-select"
                value={selectedMonth.toISOString().slice(0, 7)}
                onChange={(e) => {
                  const [y, m] = e.target.value.split("-").map(Number);
                  setSelectedMonth(new Date(y, m - 1, 1));
                }}
                className="rounded-lg border-2 border-tiber-200 px-4 py-2.5 min-h-[44px] text-juridico font-medium bg-white transition-colors duration-200 hover:border-tiber-300 focus:outline-none focus:ring-2 focus:ring-tiber-500 focus:ring-offset-2 focus:border-transparent cursor-pointer"
                aria-label={t("analytics.selectMonth")}
              >
                {getMonths().map((m) => (
                  <option
                    key={m.toISOString()}
                    value={m.toISOString().slice(0, 7)}
                  >
                    {m.toLocaleDateString(i18n.language, {
                      month: "long",
                      year: "numeric",
                    })}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center justify-center min-h-[200px]"
              >
                <div
                  className="animate-spin rounded-full h-10 w-10 border-b-2 border-tiber-500"
                  aria-label={t("common.loading")}
                />
              </motion.div>
            ) : (
              <motion.div
                key="content"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Card className="border-2 border-tiber-200 overflow-hidden">
                  <div className="overflow-x-auto -mx-px">
                    <table className="w-full min-w-[280px]">
                      <thead>
                        <tr className="bg-tiber-50">
                          <th className="text-left px-4 sm:px-6 py-4 text-sm font-semibold text-juridico">
                            {t("analytics.feature")}
                          </th>
                          <th className="text-right px-4 sm:px-6 py-4 text-sm font-semibold text-juridico">
                            {t("analytics.usage")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {FEATURE_TYPES.map((ft) => {
                          const Icon = FEATURE_ICONS[ft];
                          const count = usageSummary[ft] || 0;
                          return (
                            <tr
                              key={ft}
                              className="border-t border-tiber-100 transition-colors duration-150 hover:bg-romance-50"
                            >
                              <td className="px-4 sm:px-6 py-4 flex items-center gap-3">
                                <Icon
                                  className="w-5 h-5 text-tiber-600 shrink-0"
                                  aria-hidden
                                />
                                <span className="text-tiber-800">
                                  {getFeatureLabel(ft)}
                                </span>
                              </td>
                              <td className="px-4 sm:px-6 py-4 text-right font-medium text-juridico">
                                {count}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {Object.keys(usageSummary).length === 0 && !loading && (
                    <div className="px-6 py-12 text-center text-titan">
                      {t("analytics.noData")}
                    </div>
                  )}
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {!loading && thisMonthTotal > 0 && (
            <div className="mt-6">
              <h3 className="text-base font-semibold text-juridico mb-4">
                {t("analytics.usageByFeature")}
              </h3>
              <Card className="border-2 border-tiber-200 overflow-hidden p-4 sm:p-6">
                <div className="h-[240px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={FEATURE_TYPES.map((ft) => ({
                        name: getFeatureLabel(ft),
                        count: usageSummary[ft] || 0,
                      }))}
                      layout="vertical"
                      margin={{ top: 8, right: 24, left: 0, bottom: 8 }}
                    >
                      <XAxis
                        type="number"
                        tick={{ fill: "var(--color-tiber-600)", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={140}
                        tick={{ fill: "var(--color-tiber-700)", fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#ffffff",
                          border: "2px solid var(--color-tiber-200)",
                          borderRadius: "12px",
                          padding: "12px 16px",
                          color: "var(--color-juridico)",
                          fontSize: "14px",
                        }}
                        labelStyle={{
                          color: "var(--color-juridico)",
                          fontWeight: 600,
                        }}
                        itemStyle={{
                          color: "var(--color-tiber-700)",
                        }}
                        formatter={(value: number | undefined) => [
                          value ?? 0,
                          t("analytics.usage"),
                        ]}
                      />
                      <Bar
                        dataKey="count"
                        fill="#1cb0a9"
                        radius={[0, 4, 4, 0]}
                        maxBarSize={32}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
